/* eslint-disable no-console */
/* eslint-disable indent */
import React, { useCallback, useMemo } from "react";
import screenmediaLogo from "../../img/screenmedia-logo.png";
import visionaLogo from "../../img/visiona-logo.png";
import wecastLogo from "../../img/wecast-logo.png";
import { getLocale } from "../../utils/getLocale";
import { Document, Page, Text, View, Image, Link } from "@react-pdf/renderer";
import styles from "./stylesPdfCotizacion";

const today = new Date();
const month = String(today.getMonth() + 1).padStart(2, "0");
const day = String(today.getDate()).padStart(2, "0");
const date = `${day}-${month}-${today.getFullYear()}`;
const hour = String(today.getHours()).padStart(2, "0");
const minute = String(today.getMinutes()).padStart(2, "0");

const PdfCotizacion = ({
  ascensoresSeleccionados = 0,
  audienciaSeleccionadaAscensor = 0,
  audienciaSeleccionadaCowork = 0,
  audienciaSeleccionadaEspera = 0,
  audienciaSeleccionadaTotal = 0,
  audienciaSeleccionadaTotems = 0,
  campana = "",
  cliente = "",
  codigoGenerado,
  contactosCampañaAscensores = 0,
  contactosCampañaCowork = 0,
  contactosCampañaEspera = 0,
  contactosCampañaTotems = 0,
  coworkSeleccionados = 0,
  cpmAscensoresFinal = 0,
  cpmCoworkFinal = 0,
  cpmEsperaFinal = 0,
  cpmTotalFinal = 0,
  cpmTotemsFinal = 0,
  descuentoGlobal = 0,
  duracion = 0,
  esperaSeleccionados = 0,
  horasFinal = 0,
  impactosTotalAscensores = 0,
  impactosTotalCowork = 0,
  impactosTotalEspera = 0,
  impactosTotalTotems = 0,
  impactosTotales = 0,
  impactosEfectivosPoisson = { ascensores: 0, totems: 0, cowork: 0, espera: 0, total: 0 },
  intervalos = [],
  inversionAscensoresFinal = 0,
  inversionCoworkFinal = 0,
  inversionEsperaFinal = 0,
  inversionFinalTotal = 0,
  inversionTotemsFinal = 0,
  isProgrammaticSelected,
  nombresEdificiosNoSeleccionados = [],
  nombresEdificiosSeleccionadosAscensores = [],
  nombresEdificiosSeleccionadosCowork = [],
  nombresEdificiosSeleccionadosEspera = [],
  nombresEdificiosSeleccionadosTotems = [],
  includeInstallingBuildings = false,
  options = [],
  pais = "Chile",
  plazo = 0,
  salidasAscensores = 0,
  salidasCowork = 0,
  salidasEspera = 0,
  salidasTotales = 0,
  salidasTotems = 0,
  tarifaFriaAscensores = 0,
  tarifaFriaCowork = 0,
  tarifaFriaEspera = 0,
  tarifaFriaTotal = 0,
  tarifaFriaTotems = 0,
  totemsSeleccionados = 0,
  usuarioApellido,
  usuarioNombre,
  frecuencia,
  manualDiscountAmount = 0,
  finalAmount = 0,
  manualDiscountPercentage = 0,
  selectedMoneda = "",
  edificios = [],
}) => {
  const monedaSimbolo = useCallback(
    (pais) => {
      if (selectedMoneda === "US$") return "US$ ";
      return (
        {
          Chile: "$ ",
          Perú: "S/ ",
          Uruguay: "$ ",
        }[pais] || "CLP "
      );
    },
    [selectedMoneda]
  );

  const ciudad = useCallback(
    (pais) =>
      ({
        Chile: "Santiago",
        Perú: "Lima",
        Uruguay: "Montevideo",
      })[pais] || "Lima",
    []
  );

  const operacionTextual = useCallback(
    (pais) =>
      ({
        Chile: "WECAST",
        Perú: "VISIONA",
        Uruguay: "SCREENMEDIA",
      })[pais] || "VISIONA",
    []
  );

  const monedaTextual = useCallback(
    (pais) => {
      if (selectedMoneda === "US$") return "US$";
      return (
        {
          Chile: "CLP",
          Perú: "PEN",
          Uruguay: "URU",
        }[pais] || "CLP"
      );
    },
    [selectedMoneda]
  );

  const mailTextual = useCallback(
    (pais) =>
      ({
        Chile: "hola@wecast.cl",
        Perú: "hola@visiona.pe",
        Uruguay: "hola@screenmedia.com.uy",
      })[pais] || "hola@visiona.pe",
    []
  );

  const logo = useMemo(
    () =>
      ({
        Chile: wecastLogo,
        Perú: visionaLogo,
        Uruguay: screenmediaLogo,
      })[pais] || visionaLogo,
    [pais]
  );

  const totalPantallas = ascensoresSeleccionados + totemsSeleccionados + coworkSeleccionados + esperaSeleccionados;
  const nombreUsuario = `${usuarioNombre} ${usuarioApellido}`;
  const colWidths = {
    circuito: "13%",
    pantallas: "7%",
    audiencia: "9%",
    contactos: "10%",
    impactos: "10%",
    efectivos: "10%",
    cotizacion: "11%",
    descuento: "7%",
    inversion: "11%",
    cpm: "12%",
  };

  // Formatear horas: sin decimales si es entero, con 1 decimal si tiene .5
  const formatHoras = (horas) => {
    if (!horas) return "-";
    const hasDecimal = horas % 1 !== 0;
    return horas.toLocaleString(getLocale(pais), {
      minimumFractionDigits: hasDecimal ? 1 : 0,
      maximumFractionDigits: hasDecimal ? 1 : 0,
    });
  };

  const ordenarIntervalos = (a, b) => {
    // Validar que ambos elementos sean strings antes de hacer split
    if (typeof a !== "string" || typeof b !== "string") return 0;
    const [horaA, minutoA] = a.split(" - ")[0].split(":").map(Number);
    const [horaB, minutoB] = b.split(" - ")[0].split(":").map(Number);
    if (horaA !== horaB) return horaA - horaB;
    return minutoA - minutoB;
  };

  // const pluralizar = (cantidad, singular, plural) => {
  //   if (cantidad === 0) return "ningún edificio";
  //   return cantidad === 1 ? singular : plural;
  // };

  // Filtrar y ordenar intervalos válidos (solo strings)
  const intervalosValidos = intervalos.filter((i) => typeof i === "string").sort(ordenarIntervalos);

  const aparicionesDiarias = frecuencia && horasFinal && totalPantallas ? frecuencia * horasFinal * totalPantallas : 0;
  const aparicionesTotal = aparicionesDiarias ? aparicionesDiarias * plazo : 0;

  const contactosTotalOTS =
    (contactosCampañaAscensores || 0) +
    (contactosCampañaTotems || 0) +
    (contactosCampañaEspera || 0) +
    (contactosCampañaCowork || 0);

  const activeCircuits = [
    {
      name: "ASCENSORES",
      isActive: ascensoresSeleccionados > 0,
      pantallas: ascensoresSeleccionados,
      audiencia: audienciaSeleccionadaAscensor,
      contactos: contactosCampañaAscensores,
      impactos: impactosTotalAscensores,
      efectivos: impactosEfectivosPoisson.ascensores,
      tarifa: tarifaFriaAscensores,
      baseInversion: inversionAscensoresFinal,
      cpm: cpmAscensoresFinal,
    },
    {
      name: "TÓTEMS",
      isActive: totemsSeleccionados > 0,
      pantallas: totemsSeleccionados,
      audiencia: audienciaSeleccionadaTotems,
      contactos: contactosCampañaTotems,
      impactos: impactosTotalTotems,
      efectivos: impactosEfectivosPoisson.totems,
      tarifa: tarifaFriaTotems,
      baseInversion: inversionTotemsFinal,
      cpm: cpmTotemsFinal,
    },
    {
      name: "ESPERA",
      isActive: esperaSeleccionados > 0,
      pantallas: esperaSeleccionados,
      audiencia: audienciaSeleccionadaEspera,
      contactos: contactosCampañaEspera,
      impactos: impactosTotalEspera,
      efectivos: impactosEfectivosPoisson.espera,
      tarifa: tarifaFriaEspera,
      baseInversion: inversionEsperaFinal,
      cpm: cpmEsperaFinal,
    },
    {
      name: "COWORK iF",
      isActive: coworkSeleccionados > 0,
      pantallas: coworkSeleccionados,
      audiencia: audienciaSeleccionadaCowork,
      contactos: contactosCampañaCowork,
      impactos: impactosTotalCowork,
      efectivos: impactosEfectivosPoisson.cowork,
      tarifa: tarifaFriaCowork,
      baseInversion: inversionCoworkFinal,
      cpm: cpmCoworkFinal,
    },
  ].filter((circuit) => circuit.isActive);

  return (
    <Document>
      {(() => {
        // selectedBuildings ya está calculado en tu código (dentro del return)
        const selectedBuildings = edificios.filter(
          (b) =>
            new Set([
              ...nombresEdificiosSeleccionadosAscensores,
              ...nombresEdificiosSeleccionadosTotems,
              ...nombresEdificiosSeleccionadosEspera,
              ...nombresEdificiosSeleccionadosCowork,
            ]).has(b.edificio) &&
            b.estado &&
            (b.estado === "OPERATIVO" ||
              (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(b.estado)))
        );

        let pageHeight = 800; // Alto mínimo

        if (selectedBuildings.length > 0) {
          const rows = selectedBuildings.length + 2; // header + edificios + subtotal
          const dynamicHeight = 680 + rows * 21 + 100; // base + filas + footer
          pageHeight = Math.max(1000, dynamicHeight);
        }

        return (
          <Page size={[pageHeight, 900]} orientation="landscape" style={styles.page} wrap={pageHeight >= 2200}>
            <View style={[styles.contentContainer, { flex: 1, justifyContent: "flex-start", height: "100%" }]}>
              <Text style={styles.header} fixed>
                ~ CONFIDENCIAL ~
              </Text>
              <Text style={styles.title}>COTIZACIÓN de ESPACIOS PUBLICITARIOS en {operacionTextual(pais)}</Text>
              <Text style={styles.letrachica0}>
                {ciudad(pais)}, {date} @ {hour}:{minute} | Generado por: {nombreUsuario} | Cotización ID:{" "}
                {codigoGenerado}
              </Text>
              {(cliente || campana) && (
                <Text style={[styles.letrachica0, { marginTop: 0, fontWeight: "bold" }]}>
                  {[cliente ? `Cliente: ${cliente}` : null, campana ? `Campaña: ${campana}` : null]
                    .filter(Boolean)
                    .join(" | ")}
                </Text>
              )}

              {/* TABLA COTIZACIÓN */}
              <View style={[styles.table, { width: "100%" }]}>
                <View
                  style={[
                    styles.tableHeaderTipo,
                    {
                      backgroundColor: isProgrammaticSelected ? "#006400" : "#FF8C00",
                      alignSelf: "center",
                    },
                  ]}
                >
                  <Text>TIPO DE COMPRA: {isProgrammaticSelected ? "PROGRAMMATIC" : "DIGITAL"}</Text>
                </View>
                <View style={[styles.tableHeaderRow, styles.tableHeader]}>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.circuito, backgroundColor: "#000000", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>CIRCUITO</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.pantallas, backgroundColor: "#000000", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>PANTALLAS</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.audiencia, backgroundColor: "#000000", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>AUDIENCIA</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.contactos, backgroundColor: "#673ab7", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>CONTACTOS</Text>
                    <Text style={{ color: "#ffffff", fontSize: 7, opacity: 0.8 }}>OTS</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.impactos, backgroundColor: "#5e35b1", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>IMPACTOS</Text>
                    <Text style={{ color: "#ffffff", fontSize: 7, opacity: 0.8 }}>VAC</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.efectivos, backgroundColor: "#4527a0", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>EFECTIVOS</Text>
                    <Text style={{ color: "#ffffff", fontSize: 7, opacity: 0.8 }}>Poisson</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.cotizacion, backgroundColor: "#b8860b", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>COTIZACIÓN</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.descuento, backgroundColor: "#d97706", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>DESCUENTO</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      { width: colWidths.inversion, backgroundColor: "#28a745", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>INVERSIÓN</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCellQuote,
                      styles.lastCell,
                      { width: colWidths.cpm, backgroundColor: "#007bff", minHeight: 36 },
                    ]}
                  >
                    <Text style={{ color: "#ffffff" }}>CPM</Text>
                  </View>
                </View>

                {/* Row-based table: one View row per circuit, spanning values on midRow */}
                {(() => {
                  const fmt = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
                  const fmtCpm = pais === "Perú" ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } : fmt;
                  const sym = monedaSimbolo(pais);
                  const cellBase = {
                    paddingVertical: 5,
                    paddingHorizontal: 2,
                    borderLeftWidth: 0.3,
                    borderRightWidth: 0.3,
                    borderBottomWidth: 0.3,
                    borderColor: "#d3d3d3",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  };
                  const cellW = (width, extra = {}) => ({ ...cellBase, width, ...extra });
                  const txt = { fontSize: 9 };

                  return activeCircuits.map((c, idx) => {
                    const cpmContacto = c.contactos > 0 ? (c.baseInversion / c.contactos) * 1000 : 0;
                    return (
                      <View key={idx} style={{ flexDirection: "row" }}>
                        <View style={cellW(colWidths.circuito, { alignItems: "flex-start", paddingHorizontal: 5 })}>
                          <Text style={txt}>{c.name}</Text>
                        </View>
                        <View style={cellW(colWidths.pantallas)}>
                          <Text style={txt}>{c.pantallas || "-"}</Text>
                        </View>
                        <View style={cellW(colWidths.audiencia)}>
                          <Text style={txt}>
                            {c.audiencia ? c.audiencia.toLocaleString(getLocale(pais), fmt) : "-"}
                          </Text>
                        </View>
                        <View style={cellW(colWidths.contactos)}>
                          <Text style={txt}>
                            {c.contactos ? c.contactos.toLocaleString(getLocale(pais), fmt) : "-"}
                          </Text>
                        </View>
                        <View style={cellW(colWidths.impactos)}>
                          <Text style={txt}>{c.impactos ? c.impactos.toLocaleString(getLocale(pais), fmt) : "-"}</Text>
                        </View>
                        <View style={cellW(colWidths.efectivos)}>
                          <Text style={txt}>
                            {c.efectivos ? c.efectivos.toLocaleString(getLocale(pais), fmt) : "-"}
                          </Text>
                        </View>
                        <View style={cellW(colWidths.cotizacion)}>
                          <Text style={txt}>{`${sym}${c.tarifa.toLocaleString(getLocale(pais), fmt)}`}</Text>
                        </View>
                        <View style={cellW(colWidths.descuento)}>
                          <Text style={txt}>
                            {(() => {
                              const format = (value) => {
                                const num = value * 100;
                                const hasDecimal = !Number.isInteger(num);
                                return hasDecimal
                                  ? num.toLocaleString("es-CL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                                  : Math.round(num).toLocaleString("es-CL");
                              };
                              if (!descuentoGlobal && manualDiscountPercentage === 0) return "-";
                              const globalStr = descuentoGlobal > 0 ? format(descuentoGlobal) + "%" : null;
                              const manualStr =
                                manualDiscountPercentage > 0 ? format(manualDiscountPercentage / 100) + "%" : null;
                              if (globalStr && manualStr) return `${globalStr} + ${manualStr}`;
                              return globalStr || manualStr || "-";
                            })()}
                          </Text>
                        </View>
                        <View style={cellW(colWidths.inversion)}>
                          <Text style={txt}>
                            {`${sym} ${Math.floor(c.baseInversion).toLocaleString(getLocale(pais), fmt)}`}
                          </Text>
                        </View>
                        <View style={cellW(colWidths.cpm, { borderRightWidth: 0 })}>
                          <Text style={txt}>{`${sym} ${cpmContacto.toLocaleString(getLocale(pais), fmtCpm)}`}</Text>
                        </View>
                      </View>
                    );
                  });
                })()}

                <View style={[styles.tableRow, styles.tableTotal]}>
                  <Text style={[styles.tableCellStart, styles.tableDataTotales, { width: colWidths.circuito }]}>
                    TOTALES
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.pantallas }]}>
                    {totalPantallas || "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.audiencia }]}>
                    {audienciaSeleccionadaTotal
                      ? audienciaSeleccionadaTotal.toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.contactos }]}>
                    {contactosTotalOTS > 0
                      ? Math.round(contactosTotalOTS).toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.impactos }]}>
                    {impactosTotales > 0
                      ? Math.round(impactosTotales).toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.efectivos }]}>
                    {impactosEfectivosPoisson.total
                      ? impactosEfectivosPoisson.total.toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.cotizacion }]}>
                    {totalPantallas
                      ? `${monedaSimbolo(pais)}${tarifaFriaTotal.toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}`
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableDataTotales, { width: colWidths.descuento }]}>
                    {(() => {
                      const format = (value) => {
                        const num = value * 100;
                        const hasDecimal = !Number.isInteger(num);
                        return hasDecimal
                          ? num.toLocaleString("es-CL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                          : Math.round(num).toLocaleString("es-CL");
                      };

                      if (!descuentoGlobal && manualDiscountPercentage === 0) return "-";

                      const globalStr = descuentoGlobal > 0 ? format(descuentoGlobal) + "%" : null;
                      const manualStr =
                        manualDiscountPercentage > 0 ? format(manualDiscountPercentage / 100) + "%" : null;

                      if (globalStr && manualStr) {
                        return `${globalStr} + ${manualStr}`;
                      }
                      return globalStr || manualStr || "-";
                    })()}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: colWidths.inversion }]}>
                    {`${monedaSimbolo(pais)} ${
                      manualDiscountAmount > 0
                        ? finalAmount.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : inversionFinalTotal.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                    }`}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableDataTotales, styles.lastCell, { width: colWidths.cpm }]}>
                    {`${monedaSimbolo(pais)} ${
                      contactosTotalOTS > 0
                        ? ((inversionFinalTotal / contactosTotalOTS) * 1000).toLocaleString(getLocale(pais), {
                            minimumFractionDigits: pais === "Perú" ? 1 : 0,
                            maximumFractionDigits: pais === "Perú" ? 1 : 0,
                          })
                        : "-"
                    }`}
                  </Text>
                </View>
              </View>

              {/* Párrafo narrativo */}
              <View
                style={[styles.parrafoNarrativo, { borderLeftColor: isProgrammaticSelected ? "#006400" : "#FF8C00" }]}
              >
                <Text style={styles.parrafoNarrativoText}>
                  {(() => {
                    const numEdificios = new Set([
                      ...nombresEdificiosSeleccionadosAscensores,
                      ...nombresEdificiosSeleccionadosTotems,
                      ...nombresEdificiosSeleccionadosEspera,
                      ...nombresEdificiosSeleccionadosCowork,
                    ]).size;
                    const aparicionesPorHora = frecuencia * totalPantallas;
                    const cpmReproduccion = aparicionesTotal > 0 ? (inversionFinalTotal / aparicionesTotal) * 1000 : 0;
                    const cpmContacto = contactosTotalOTS > 0 ? (inversionFinalTotal / contactosTotalOTS) * 1000 : 0;
                    const cpmImpacto = cpmTotalFinal;
                    const cpmEfectivo =
                      impactosEfectivosPoisson.total > 0
                        ? (inversionFinalTotal / impactosEfectivosPoisson.total) * 1000
                        : 0;
                    const fmt = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
                    const fmt1 = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
                    const sym = monedaSimbolo(pais);
                    const locale = getLocale(pais);
                    let horarioTexto = "";
                    if (intervalosValidos.length > 0) {
                      horarioTexto = `intervalos seleccionados (${formatHoras(horasFinal)}h)`;
                    } else if (options.length > 0) {
                      const label = options[0].label;
                      if (label.includes("FULL")) horarioTexto = "Full Day (07:00 a 21:00)";
                      else if (label.includes("AM")) horarioTexto = "AM (07:00 a 14:00)";
                      else if (label.includes("PM")) horarioTexto = "PM (14:00 a 21:00)";
                      else horarioTexto = label;
                    }
                    const descPct = (descuentoGlobal * 100).toLocaleString(locale, fmt1);
                    return `Tu pauta se emitirá en ${numEdificios} edificios durante ${plazo} días (lunes a viernes), con ${aparicionesPorHora.toLocaleString(locale, fmt)} apariciones por hora (${frecuencia} reproducciones por hora por pantalla) a través de ${totalPantallas} pantallas en horario ${horarioTexto}, conectando con ${audienciaSeleccionadaTotal.toLocaleString(locale, fmt)} personas únicas y generando ${Math.round(contactosTotalOTS).toLocaleString(locale, fmt)} contactos en el plazo de la campaña. La cotización asciende a ${sym}${tarifaFriaTotal.toLocaleString(locale, fmt)}, con un descuento del ${descPct}%, resultando en una inversión final de ${sym}${inversionFinalTotal.toLocaleString(locale, fmt)}. CPM por reproducción ${sym}${Math.round(cpmReproduccion).toLocaleString(locale, fmt)}, CPM por contacto ${sym}${Math.round(cpmContacto).toLocaleString(locale, fmt)}, CPM por impacto ${sym}${Math.round(cpmImpacto).toLocaleString(locale, fmt)}, CPM por impacto efectivo ${sym}${Math.round(cpmEfectivo).toLocaleString(locale, fmt)}.`;
                  })()}
                </Text>
              </View>

              <View style={styles.hr} />

              <View style={styles.datosCampanaContainer}>
                <View style={styles.datosCampanaRow}>
                  <View style={styles.datosCampanaCell}>
                    <View style={styles.datosCampanaHeader}>
                      <Text style={styles.datosCampanaLabel}>PLAZO</Text>
                    </View>
                    <View style={styles.datosCampanaValueWrap}>
                      <Text style={styles.datosCampanaValue}>{plazo} días (lunes a viernes)</Text>
                    </View>
                  </View>
                  <View style={[styles.datosCampanaCell, styles.datosCampanaCellLast]}>
                    <View style={styles.datosCampanaHeader}>
                      <Text style={styles.datosCampanaLabel}>EXHIBICIÓN</Text>
                    </View>
                    <View style={styles.datosCampanaValueWrap}>
                      <Text style={styles.datosCampanaValue}>
                        {intervalosValidos.length > 0
                          ? `Intervalos: ${intervalosValidos.join(" | ")} = ${formatHoras(horasFinal)} horas por día`
                          : options.length > 0
                            ? `${options[0].label} = ${formatHoras(horasFinal)} horas por día`
                            : `${formatHoras(horasFinal)} horas por día`}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.datosCampanaRow}>
                  <View style={styles.datosCampanaCell}>
                    <View style={styles.datosCampanaHeader}>
                      <Text style={styles.datosCampanaLabel}>FRECUENCIA</Text>
                    </View>
                    <View style={styles.datosCampanaValueWrap}>
                      <Text style={styles.datosCampanaValue}>
                        {`${(frecuencia * totalPantallas).toLocaleString(getLocale(pais), { minimumFractionDigits: 0, maximumFractionDigits: 0 })} apariciones por hora, ${aparicionesDiarias ? aparicionesDiarias.toLocaleString(getLocale(pais), { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "-"} por día, ${aparicionesTotal ? aparicionesTotal.toLocaleString(getLocale(pais), { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "-"} por campaña`}
                      </Text>
                    </View>
                  </View>
                  <View style={[styles.datosCampanaCell, styles.datosCampanaCellLast]}>
                    <View style={styles.datosCampanaHeader}>
                      <Text style={styles.datosCampanaLabel}>DURACIÓN DEL CONTENIDO</Text>
                    </View>
                    <View style={styles.datosCampanaValueWrap}>
                      <Text style={styles.datosCampanaValue}>{duracion} segundos</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>INVENTARIO CONTRATADO</Text>

                <Text style={styles.inventariosubTitulo}>
                  {selectedBuildings.length} {selectedBuildings.length === 1 ? "edificio" : "edificios"} ·{" "}
                  {totalPantallas} {totalPantallas === 1 ? "pantalla" : "pantallas"}
                </Text>
                <Text style={[styles.inventarioNombres]}>
                  {[
                    ascensoresSeleccionados ? `${ascensoresSeleccionados} pantallas en ascensores` : null,
                    totemsSeleccionados ? `${totemsSeleccionados} tótems de circulación` : null,
                    esperaSeleccionados ? `${esperaSeleccionados} pantallas de espera` : null,
                    coworkSeleccionados ? `${coworkSeleccionados} pantallas en cowork` : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}{" "}
                  {![ascensoresSeleccionados, totemsSeleccionados, esperaSeleccionados, coworkSeleccionados].some(
                    Boolean
                  ) && "-"}
                </Text>

                {(() => {
                  // === 1. Obtener IDs seleccionados por circuito ===
                  const ascensoresIds = new Set(
                    nombresEdificiosSeleccionadosAscensores
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  const totemsIds = new Set(
                    nombresEdificiosSeleccionadosTotems
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  const esperaIds = new Set(
                    nombresEdificiosSeleccionadosEspera
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  const coworkIds = new Set(
                    nombresEdificiosSeleccionadosCowork
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  // === 2. Determinar si hay cowork ===
                  const hasCowork = coworkIds.size > 0;
                  const hasAscensores = ascensoresSeleccionados > 0;
                  const hasCirculacion = totemsSeleccionados > 0;
                  const hasEspera = esperaSeleccionados > 0;

                  // === 3. Todos los IDs seleccionados (sin duplicar) ===
                  const selectedIds = new Set([...ascensoresIds, ...totemsIds, ...esperaIds, ...coworkIds]);

                  // === 4. Filtrar edificios válidos + seleccionados ===
                  const selectedBuildings = edificios
                    .filter(
                      (b) =>
                        selectedIds.has(b.id) &&
                        b.estado &&
                        (b.estado === "OPERATIVO" ||
                          (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(b.estado)))
                    )
                    .map((b) => {
                      const asc = ascensoresIds.has(b.id) ? b.pantallas || 0 : 0;
                      const cir = totemsIds.has(b.id) ? b.totems || 0 : 0;
                      const esp = esperaIds.has(b.id) ? b.espera || 0 : 0;
                      const cow = coworkIds.has(b.id) ? b.cowork || 0 : 0;
                      const total = asc + cir + esp + cow;
                      const pantallasAjustadas =
                        (ascensoresIds.has(b.id) ? b.pantallas || 0 : 0) +
                        (totemsIds.has(b.id) ? b.totems || 0 : 0) +
                        (esperaIds.has(b.id) ? b.espera || 0 : 0) +
                        (coworkIds.has(b.id) ? b.cowork || 0 : 0);
                      const pantallastotales =
                        ascensoresSeleccionados + totemsSeleccionados + esperaSeleccionados + coworkSeleccionados;
                      const inversion =
                        pantallastotales > 0 ? (pantallasAjustadas / pantallastotales) * inversionFinalTotal : 0;
                      return {
                        nombre: b.edificio,
                        ubicacion: b.ubicacion || "-",
                        direccion: b.direccion || "-",
                        lat: b.lat,
                        lng: b.lng,
                        ascensores: asc > 0 ? asc : "-",
                        circulacion: cir > 0 ? cir : "-",
                        espera: esp > 0 ? esp : "-",
                        cowork: cow > 0 ? cow : "-",
                        total: total > 0 ? total : "-",
                        inversion,
                      };
                    })
                    .sort((a, b) => a.nombre.localeCompare(b.nombre));

                  // === 5. Subtotales ===
                  const subtotalAsc = selectedBuildings.reduce((s, b) => s + (parseInt(b.ascensores) || 0), 0);
                  const subtotalCir = selectedBuildings.reduce((s, b) => s + (parseInt(b.circulacion) || 0), 0);
                  const subtotalEsp = selectedBuildings.reduce((s, b) => s + (parseInt(b.espera) || 0), 0);
                  const subtotalCow = selectedBuildings.reduce((s, b) => s + (parseInt(b.cowork) || 0), 0);
                  const totalGeneral = subtotalAsc + subtotalCir + subtotalEsp + subtotalCow;

                  if (selectedBuildings.length === 0) {
                    return <Text style={styles.inventariosubTitulo}>0 edificios seleccionados</Text>;
                  }

                  // === 6. Calcular anchos dinámicos (optimizado para direcciones largas) ===
                  const baseWidth = hasCowork ? 4 : 8;
                  const edificioWidth = hasCowork ? 18 : 22;
                  const totalWidth = hasCowork ? 6 : 11;
                  const colWidth = hasCowork ? 7 : 13;
                  const direccionWidth = hasCowork ? 20 : 22;
                  const inversionWidth = 12;
                  const inactiveCircuits = [!hasAscensores, !hasCirculacion, !hasEspera, !hasCowork].filter(
                    Boolean
                  ).length;
                  const direccionWidthFinal = direccionWidth + inactiveCircuits * colWidth;

                  return (
                    <>
                      <View style={styles.inventarioTable}>
                        {/* Header */}
                        <View style={styles.inventarioHeaderRow}>
                          <Text style={[styles.inventarioHeaderCell, { width: `${baseWidth}%` }]}>#</Text>
                          <Text
                            style={[
                              styles.inventarioHeaderCell,
                              { width: `${edificioWidth}%`, textAlign: "left", paddingLeft: 8 },
                            ]}
                          >
                            Edificio
                          </Text>
                          <Text
                            style={[styles.inventarioHeaderCell, { width: "14%", textAlign: "left", paddingLeft: 8 }]}
                          >
                            Ubicación
                          </Text>
                          <Text
                            style={[
                              styles.inventarioHeaderCell,
                              { width: `${direccionWidthFinal}%`, textAlign: "left", paddingLeft: 8 },
                            ]}
                          >
                            Dirección
                          </Text>
                          {hasAscensores && (
                            <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>ASC.</Text>
                          )}
                          {hasCirculacion && (
                            <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>CIR.</Text>
                          )}
                          {hasEspera && (
                            <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>ESP.</Text>
                          )}
                          {hasCowork && (
                            <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>COW.</Text>
                          )}
                          <Text style={[styles.inventarioHeaderCell, { width: `${totalWidth}%` }]}>Total</Text>
                          <Text style={[styles.inventarioHeaderCell, { width: `${inversionWidth}%`, borderRight: 0 }]}>
                            Inversión
                          </Text>
                        </View>

                        {/* Filas */}
                        {selectedBuildings.map((b, i) => (
                          <View style={i % 2 === 0 ? styles.inventarioRow : styles.inventarioRowAlt} key={i}>
                            <Text style={[styles.inventarioCell, { width: `${baseWidth}%` }]}>{i + 1}</Text>
                            <Text style={[styles.inventarioCellLeft, { width: `${edificioWidth}%` }]}>{b.nombre}</Text>
                            <Text style={[styles.inventarioCellLeft, { width: "12%" }]}>{b.ubicacion}</Text>{" "}
                            <Text style={[styles.inventarioCellLeft, { width: `${direccionWidthFinal}%` }]}>
                              <Link src={`https://www.google.com/maps?q=${b.lat},${b.lng}`}>{b.direccion}</Link>
                            </Text>
                            {hasAscensores && (
                              <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.ascensores}</Text>
                            )}
                            {hasCirculacion && (
                              <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.circulacion}</Text>
                            )}
                            {hasEspera && (
                              <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.espera}</Text>
                            )}
                            {hasCowork && (
                              <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.cowork}</Text>
                            )}
                            <Text
                              style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${totalWidth}%` }]}
                            >
                              {b.total}
                            </Text>
                            <Text
                              style={[
                                styles.inventarioCell,
                                {
                                  width: `${inversionWidth}%`,
                                  borderRight: 0,
                                  textAlign: "right",
                                  paddingRight: 4,
                                },
                              ]}
                            >
                              {`${monedaSimbolo(pais)}${Math.round(b.inversion).toLocaleString(getLocale(pais), {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}`}
                            </Text>
                          </View>
                        ))}

                        {/* Subtotales */}
                        <View style={styles.inventarioFooterRow}>
                          <Text
                            style={[
                              styles.inventarioCell,
                              {
                                width: `${baseWidth + edificioWidth + 12 + direccionWidthFinal}%`,
                                textAlign: "right",
                                paddingRight: 8,
                                borderRight: 0,
                              },
                            ]}
                          />
                          {hasAscensores && (
                            <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                              {subtotalAsc}
                            </Text>
                          )}
                          {hasCirculacion && (
                            <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                              {subtotalCir}
                            </Text>
                          )}
                          {hasEspera && (
                            <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                              {subtotalEsp}
                            </Text>
                          )}
                          {hasCowork && (
                            <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                              {subtotalCow}
                            </Text>
                          )}
                          <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${totalWidth}%` }]}>
                            {totalGeneral}
                          </Text>
                          <Text
                            style={[
                              styles.inventarioCell,
                              styles.inventarioCellBold,
                              {
                                width: `${inversionWidth}%`,
                                borderRight: 0,
                                textAlign: "right",
                                paddingRight: 4,
                              },
                            ]}
                          >
                            {`${monedaSimbolo(pais)}${Math.round(
                              selectedBuildings.reduce((s, b) => s + (b.inversion || 0), 0)
                            ).toLocaleString(getLocale(pais), {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}`}
                          </Text>
                        </View>
                      </View>
                    </>
                  );
                })()}
              </View>

              {/* EDIFICIOS NO SELECCIONADOS + línea separadora (solo si existen) */}
              {nombresEdificiosNoSeleccionados.length > 0 && (
                <>
                  <View style={styles.detallesSeccionNoIncluidos}>
                    <Text style={styles.detallesTitulo}>EDIFICIOS DISPONIBLES NO INCLUIDOS EN ESTA PROPUESTA</Text>
                    <Text style={styles.inventarioNombres}>
                      {nombresEdificiosNoSeleccionados.sort((a, b) => a.localeCompare(b)).join(" // ")}
                    </Text>
                  </View>
                </>
              )}

              <View style={styles.hr} />

              <View style={[styles.footer, { marginTop: 0 }]}>
                <Text style={styles.letrachica2}>
                  Propuesta exclusiva para agencia y/o cliente cotizado. Importes expresados en {monedaTextual(pais)} y
                  no incluyen impuestos. Exhibición de lunes a viernes de 7 AM a 9 PM según frecuencia y horario
                  contratado. Propuesta incluye descuentos especiales que no podrán considerarse como antecedente. El
                  cliente podrá cambiar materiales tantas veces como desee. Cotización válida para pautas a emitirse
                  hasta diciembre 2026 inclusive.
                </Text>
                <Image style={styles.image} src={logo} />
                <Text style={styles.mail}>{mailTextual(pais)}</Text>
              </View>
            </View>
          </Page>
        );
      })()}
    </Document>
  );
};

export default PdfCotizacion;
